import { LinePickingSnappingTargetMode } from '../../store/selectors/lineSnappingSelectors';
import { GeometricEcosystem } from '../layout-geometry/ecosystem/GeometricEcosystem';
import { EntitySnapTarget, getBearingEnvelopeSnapType, SerializedEntitySnapTarget } from './EntitySnapTarget';
import { EntitySnapTargetSelectionSettings } from './settings';

export class EntitySnapTargetSource {
  private readonly targets: EntitySnapTarget[];

  constructor(ecosystem: GeometricEcosystem) {
    this.targets = [];

    for (const snap of ecosystem.getRoofSnapsWithSources()) {
      this.targets.push(
        new EntitySnapTarget('roof', snap.roofPlaneGuid ?? snap.roofContainerGuid ?? null, snap.geometry),
      );
    }

    for (const snap of ecosystem.getCeilingSnapsWithSources()) {
      this.targets.push(
        new EntitySnapTarget('ceiling', snap.roofPlaneGuid ?? snap.roofContainerGuid ?? null, snap.geometry),
      );
    }

    for (const envelopeData of ecosystem.getTrussEnvelopes()) {
      const {
        frontSegmentWithBevelsConsidered: frontSegment,
        backSegmentWithBevelsConsidered: backSegment,
        middleSegmentWithBevelsConsidered: middleSegment,
      } = envelopeData;

      this.targets.push(new EntitySnapTarget('truss-envelope', null, backSegment));
      this.targets.push(new EntitySnapTarget('truss-envelope', null, middleSegment));
      this.targets.push(new EntitySnapTarget('truss-envelope', null, frontSegment));
    }

    const bearingEnvelopes = ecosystem.getBearingEnvelopes();
    for (const [guid, polygon] of Object.entries(ecosystem.getBearingAreas())) {
      for (const edge of polygon.edges) {
        this.targets.push(new EntitySnapTarget(getBearingEnvelopeSnapType(bearingEnvelopes[guid]!), guid, edge));
      }
    }

    for (const [guid, line] of Object.entries(ecosystem.getWorkLineDictionary())) {
      this.targets.push(new EntitySnapTarget('work-line', guid, line));
    }

    for (const [guid, segment] of Object.entries(ecosystem.getUploadedSnappingLines())) {
      this.targets.push(new EntitySnapTarget('uploaded-line', guid, segment));
    }

    for (const polygon of ecosystem.getFeaturePatternContainerPolygonsIn2D()) {
      for (const edge of polygon.edges) {
        this.targets.push(new EntitySnapTarget('feature-pattern-container-edge', null, edge));
      }
    }

    // The insertion feature pattern container has its own type. When picking the "edge to slope from" of an insertion
    //   container, its own edges are the only valid target.
    const insertionFeaturePatternContainer = ecosystem.getFocusedFeaturePatternContainerPolygonIn2D();
    if (insertionFeaturePatternContainer) {
      for (const edge of insertionFeaturePatternContainer.edges) {
        this.targets.push(new EntitySnapTarget('insertion-feature-pattern-container-edge', null, edge));
      }
    }
  }

  getAllActiveTargets(): EntitySnapTarget[] {
    return [...this.targets];
  }

  getFilteredTargets(
    predicate: (target: EntitySnapTarget, index: number, array: EntitySnapTarget[]) => boolean,
  ): EntitySnapTarget[] {
    return this.targets.filter(predicate);
  }

  getActiveTargetsForSelectionSettings(settings: EntitySnapTargetSelectionSettings) {
    const enabledTargets = new Set(settings.enabledTargetTypes);
    return this.getFilteredTargets((target) => enabledTargets.has(target.type));
  }

  getActiveTargetsForLineSnapping(mode: LinePickingSnappingTargetMode) {
    return this.getFilteredTargets((target) => mode.includes(target.type));
  }

  getActiveTargetsForTrussEnvelopeSnapping(
    settings: EntitySnapTargetSelectionSettings,
    lockedTarget?: SerializedEntitySnapTarget,
  ) {
    return lockedTarget // if the user has locked in to a specific target, use that with no other considerations
      ? [EntitySnapTarget.fromObject(lockedTarget)]
      : this.getActiveTargetsForSelectionSettings(settings);
  }
}
